import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { Cog, Calendar, Clock, Plus, CheckCircle, AlertCircle, Wrench } from 'lucide-react';

function MachinePlanning() {
  const { API } = useContext(AppContext);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchMachineData();
  }, [selectedDate]);

  const fetchMachineData = async () => {
    setLoading(true);
    // Mock data
    setMachines([
      { id: '1', code: 'MCH-001', name: 'Shredder Machine A', status: 'Running', currentJob: 'Aluminum Scrap Processing', startTime: '06:00', endTime: '14:00', operator: 'Vijay Kumar', efficiency: 92 },
      { id: '2', code: 'MCH-002', name: 'Melting Furnace 1', status: 'Running', currentJob: 'Zinc Ingot Melting', startTime: '06:00', endTime: '18:00', operator: 'Anil Sharma', efficiency: 88 },
      { id: '3', code: 'MCH-003', name: 'Casting Machine', status: 'Scheduled', currentJob: 'Copper Bar Casting', startTime: '14:00', endTime: '22:00', operator: 'Rakesh Verma', efficiency: 0 },
      { id: '4', code: 'MCH-004', name: 'Shredder Machine B', status: 'Maintenance', currentJob: 'Blade Replacement', startTime: '-', endTime: '-', operator: 'Maintenance Team', efficiency: 0 },
      { id: '5', code: 'MCH-005', name: 'Sorting Conveyor', status: 'Running', currentJob: 'Ferrous Sorting', startTime: '06:00', endTime: '22:00', operator: 'Sunil Das', efficiency: 95 },
      { id: '6', code: 'MCH-006', name: 'Baling Press', status: 'Idle', currentJob: '-', startTime: '-', endTime: '-', operator: '-', efficiency: 0 },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Running': return 'bg-green-100 text-green-700 border-green-200';
      case 'Scheduled': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Maintenance': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Idle': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Running': return <CheckCircle size={14} />;
      case 'Scheduled': return <Clock size={14} />;
      case 'Maintenance': return <Wrench size={14} />;
      case 'Idle': return <AlertCircle size={14} />;
      default: return null;
    }
  };

  const stats = {
    running: machines.filter(m => m.status === 'Running').length,
    scheduled: machines.filter(m => m.status === 'Scheduled').length,
    maintenance: machines.filter(m => m.status === 'Maintenance').length,
    idle: machines.filter(m => m.status === 'Idle').length,
  };

  if (loading) {
    return <div className="text-center py-12">Loading machine data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="machine-planning-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Machine Planning</h1>
          <p className="text-gray-500 mt-1">Schedule and monitor machine operations</p>
        </div>
        <div className="flex items-center space-x-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
          />
          <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
            <Plus size={18} />
            <span>Schedule Job</span>
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-green-500 rounded-lg">
              <CheckCircle className="text-white" size={18} />
            </div>
            <div>
              <p className="text-sm text-green-600">Running</p>
              <p className="text-2xl font-bold text-green-700">{stats.running}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Clock className="text-white" size={18} />
            </div>
            <div>
              <p className="text-sm text-blue-600">Scheduled</p>
              <p className="text-2xl font-bold text-blue-700">{stats.scheduled}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-orange-500 rounded-lg">
              <Wrench className="text-white" size={18} />
            </div>
            <div>
              <p className="text-sm text-orange-600">Maintenance</p>
              <p className="text-2xl font-bold text-orange-700">{stats.maintenance}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-gray-500 rounded-lg">
              <AlertCircle className="text-white" size={18} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Idle</p>
              <p className="text-2xl font-bold text-gray-700">{stats.idle}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Machine Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {machines.map((machine) => (
          <div key={machine.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-mono text-gray-500">{machine.code}</p>
                <h3 className="text-lg font-bold text-gray-900">{machine.name}</h3>
              </div>
              <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(machine.status)}`}>
                {getStatusIcon(machine.status)}
                <span>{machine.status}</span>
              </span>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500">Current Job</p>
                <p className="text-sm font-medium text-gray-900">{machine.currentJob}</p>
              </div>
              
              {machine.status === 'Running' && (
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Efficiency</span>
                    <span className="font-medium text-gray-900">{machine.efficiency}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${machine.efficiency >= 90 ? 'bg-green-500' : machine.efficiency >= 70 ? 'bg-blue-500' : 'bg-orange-500'}`}
                      style={{ width: `${machine.efficiency}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="text-xs">
                  <span className="text-gray-500">Time: </span>
                  <span className="font-mono text-gray-900">{machine.startTime} - {machine.endTime}</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="h-6 w-6 bg-gradient-to-br from-[#e65100] to-[#f57c00] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-xs">{machine.operator.charAt(0)}</span>
                </div>
                <span className="text-sm text-gray-600">{machine.operator}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MachinePlanning;
